import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton
} from '@mui/material';
import InputField from 'src/components/InputField';
import MultilineInputField from 'src/components/MultilineInputField';
import { api } from 'src/Api';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CustomDropdown from 'src/components/CustomDropdown';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import Iconify from 'src/components/Iconify';
import ErrorIcon from '@mui/icons-material/Error';

const AddEditTaskModal = ({
  open,
  onClose,
  refresh,
  selectedTaskData,
  sub_project_id,
  project_id
}) => {
  const theme = useTheme();
  const token = localStorage.getItem('token');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [saveLoading, setSaveLoading] = useState(false);
  const [conditionListData, setConidtionListData] = useState([]);
  const [unitListData, setUnitListData] = useState([]);
  const [activityListData, setActivityListData] = useState([]);
  const [depreciaationListData, setDepreciaationListData] = useState([]);
  const [confirmationListData, setConfirmationListData] = useState([]);
  const [categoryListData, setCategoryListData] = useState([]);

  // Direct state management - no Formik
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    age: '',
    condition: '',
    qty: '',
    itemAmount: '',
    reportedCost: '',
    unitCost: '',
    unit: '',
    coverage: '',
    activity: '',
    workersWage: '',
    laborBurden: '',
    laborOverhead: '',
    material: '',
    equipment: '',
    marketCondition: '',
    laborMinimum: '',
    salesTax: '',
    rcv: '',
    life: '',
    depreciation: '',
    depreciationAmount: '',
    recoverable: '',
    acv: '',
    tax: '',
    replace: '',
    cat: '',
    sel: '',
    owner: '',
    originalVendor: '',
    sourceName: '',
    date: null,
    notes: '',
    adjSource: ''
  });

  const [errors, setErrors] = useState({});

  // Validation schema
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.date || !dayjs(formData.date).isValid()) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Initialize form data when modal opens or selectedTaskData changes
  useEffect(() => {
    if (open && selectedTaskData) {
      setFormData({
        code: selectedTaskData?.code || '',
        name: selectedTaskData?.name || '',
        description: selectedTaskData?.description || '',
        age: selectedTaskData?.age || '',
        condition: selectedTaskData?.condition_id || '',
        qty: selectedTaskData?.qty || '',
        itemAmount: selectedTaskData?.item_amount || '',
        reportedCost: selectedTaskData?.reported_cost || '',
        unitCost: selectedTaskData?.unit_cost || '',
        unit: selectedTaskData?.unit_id || '',
        coverage: selectedTaskData?.coverage || '',
        activity: selectedTaskData?.activity_id || '',
        workersWage: selectedTaskData?.worker_wage || '',
        laborBurden: selectedTaskData?.labor_burden || '',
        laborOverhead: selectedTaskData?.labor_overhead || '',
        material: selectedTaskData?.material || '',
        equipment: selectedTaskData?.equipment || '',
        marketCondition: selectedTaskData?.market_condition || '',
        laborMinimum: selectedTaskData?.labor_minimum || '',
        salesTax: selectedTaskData?.sales_tax || '',
        rcv: selectedTaskData?.rcv || '',
        life: selectedTaskData?.life || '',
        depreciation: selectedTaskData?.depreciation_type_id || '',
        depreciationAmount: selectedTaskData?.depreciation_amount || '',
        recoverable: selectedTaskData?.recoverable || '',
        acv: selectedTaskData?.acv || '',
        tax: selectedTaskData?.tax || '',
        replace: selectedTaskData?.replace || '',
        cat: selectedTaskData?.cat || '',
        sel: selectedTaskData?.sel || '',
        owner: selectedTaskData?.owner || '',
        originalVendor: selectedTaskData?.original_vendor || '',
        sourceName: selectedTaskData?.source_name || '',
        date: dayjs(selectedTaskData?.date) || null,
        notes: selectedTaskData?.note || '',
        adjSource: selectedTaskData?.adj_source || ''
      });
      setErrors({});
    }
  }, [open, selectedTaskData]);

  useEffect(() => {
    if (open) {
      getConidtionList();
      getUnitList();
      getActivityList();
      getDepreciationList();
      getConfirmationList();
      getCategoryList();
    }
  }, [open]);

  // GET CONDITIONS
  function getConidtionList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_conditions, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setConidtionListData(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  // GET UNIT
  function getUnitList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_units, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setUnitListData(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  // GET ACTIVITY
  function getActivityList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_activities, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setActivityListData(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  // GET DEPRECIATIONS
  function getDepreciationList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_depreciations, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setDepreciaationListData(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  // GET CONFIRMATION
  function getConfirmationList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_confirmations, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setConfirmationListData(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  // GET CATEGORY
  function getCategoryList() {
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(api.get_categories, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setCategoryListData(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  // Direct state update functions
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // agar valid date select ho gayi to error hata do
    if (value && dayjs(value).isValid()) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setSaveLoading(true);
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formDataToSend = new FormData();

    selectedTaskData?.id && formDataToSend.append('id', selectedTaskData?.id);
    project_id && formDataToSend.append('project_id', project_id);
    sub_project_id && formDataToSend.append('sub_project_id', sub_project_id);

    formData.code && formDataToSend.append('code', formData.code);
    formData?.name && formDataToSend.append('name', formData?.name);
    formData?.description && formDataToSend.append('description', formData?.description);
    formData?.age && formDataToSend.append('age', formData?.age);
    formData?.condition && formDataToSend.append('condition_id', formData?.condition);
    formData?.qty && formDataToSend.append('qty', formData?.qty);
    formData?.itemAmount && formDataToSend.append('item_amount', formData?.itemAmount);
    formData?.reportedCost && formDataToSend.append('reported_cost', formData?.reportedCost);
    formData?.unitCost && formDataToSend.append('unit_cost', formData?.unitCost);
    formData?.unit && formDataToSend.append('unit_id', formData?.unit);
    formData?.coverage && formDataToSend.append('coverage', formData?.coverage);
    formData?.activity && formDataToSend.append('activity_id', formData?.activity);
    formData?.workersWage && formDataToSend.append('worker_wage', formData?.workersWage);
    formData?.laborBurden && formDataToSend.append('labor_burden', formData?.laborBurden);
    formData?.laborOverhead && formDataToSend.append('labor_overhead', formData?.laborOverhead);
    formData?.material && formDataToSend.append('material', formData?.material);
    formData?.equipment && formDataToSend.append('equipment', formData?.equipment);
    formData?.marketCondition &&
      formDataToSend.append('market_condition', formData?.marketCondition);
    formData?.laborMinimum && formDataToSend.append('labor_minimum', formData?.laborMinimum);
    formData?.salesTax && formDataToSend.append('sales_tax', formData?.salesTax);
    formData?.rcv && formDataToSend.append('rcv', formData?.rcv);
    formData?.life && formDataToSend.append('life', formData?.life);
    formData?.depreciation && formDataToSend.append('depreciation_type_id', formData?.depreciation);
    formData?.depreciationAmount &&
      formDataToSend.append('depreciation_amount', formData?.depreciationAmount);
    formData?.recoverable && formDataToSend.append('recoverable', formData?.recoverable);
    formData?.acv && formDataToSend.append('acv', formData?.acv);
    formData?.tax && formDataToSend.append('tax', formData?.tax);
    formData?.replace && formDataToSend.append('replace', formData?.replace);
    formData?.cat && formDataToSend.append('cat', formData?.cat);
    formData?.sel && formDataToSend.append('sel', formData?.sel);
    formData?.owner && formDataToSend.append('owner', formData?.owner);
    formData?.originalVendor && formDataToSend.append('original_vendor', formData?.originalVendor);
    formData?.sourceName && formDataToSend.append('source_name', formData?.sourceName);
    formData?.date && formDataToSend.append('date', dayjs(formData?.date).format('YYYY-MM-DD'));
    formData?.notes && formDataToSend.append('note', formData?.notes);
    formData?.adjSource && formDataToSend.append('adj_source', formData?.adjSource);

    const requestOptions = {
      method: 'POST',
      redirect: 'follow',
      headers: myHeaders,
      body: formDataToSend
    };

    fetch(api.add_edit_task, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          onClose();
          refresh();
          resetForm();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      age: '',
      condition: '',
      qty: '',
      itemAmount: '',
      reportedCost: '',
      unitCost: '',
      unit: '',
      coverage: '',
      activity: '',
      workersWage: '',
      laborBurden: '',
      laborOverhead: '',
      material: '',
      equipment: '',
      marketCondition: '',
      laborMinimum: '',
      salesTax: '',
      rcv: '',
      life: '',
      depreciation: '',
      depreciationAmount: '',
      recoverable: '',
      acv: '',
      tax: '',
      replace: '',
      cat: '',
      sel: '',
      owner: '',
      originalVendor: '',
      sourceName: '',
      date: null,
      notes: '',
      adjSource: ''
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: 'auto',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: palette.primary.main,
          height: 50
        }}
      >
        <Typography sx={{ fontSize: 20, fontFamily: 'poppinsBold', color: palette.primary.white }}>
          {selectedTaskData ? 'Edit Task' : 'Add New Task'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon sx={{ color: '#fff' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          overflowY: 'auto',
          py: 2
        }}
      >
        <Grid container spacing={1}>
          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: 18,
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.main,
                mb: 1
              }}
            >
              Basic Information
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="code"
              label="Code"
              value={formData.code}
              onChange={handleChange}
              backgroundColor="#fff"
              type="text"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="name"
              label="Name *"
              value={formData.name}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              error={Boolean(errors.name)}
              helperText={errors.name}
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="age"
              label="Age"
              type="number"
              value={formData.age}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12}>
            <MultilineInputField
              name="description"
              label="Description *"
              value={formData.description}
              onChange={handleChange}
              backgroundColor="#fff"
              rows={3}
              maxRows={8}
              fullWidth
              error={Boolean(errors.description)}
              helperText={errors.description}
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              label="Condition"
              value={formData.condition}
              onChange={(e) => handleDropdownChange('condition', e.target.value)}
              options={conditionListData.map((val) => ({
                value: val.resource_id,
                label: val.name
              }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              label="Unit"
              value={formData.unit}
              onChange={(e) => handleDropdownChange('unit', e.target.value)}
              options={unitListData.map((val) => ({ value: val.resource_id, label: val.name }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="coverage"
              label="Coverage"
              value={formData.coverage}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          {/* Cost Details Section */}
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: 18,
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.main,
                mb: 1
              }}
            >
              Cost Details
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="qty"
              label="Quantity"
              value={formData.qty}
              onChange={handleChange}
              backgroundColor="#fff"
              type="number"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="itemAmount"
              label="Item Amount ($)"
              type="number"
              value={formData.itemAmount}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="reportedCost"
              label="Reported Cost ($)"
              type="number"
              value={formData.reportedCost}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="unitCost"
              label="Unit Cost ($)"
              type="number"
              value={formData.unitCost}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          {/* Labor Details */}
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: 18,
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.main,
                mb: 1
              }}
            >
              Labor Details
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="workersWage"
              label="Worker's Wage ($)"
              type="number"
              value={formData.workersWage}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="laborBurden"
              label="Labor Burden ($)"
              type="number"
              value={formData.laborBurden}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="laborOverhead"
              label="Labor Overhead"
              type="number"
              value={formData.laborOverhead}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="laborMinimum"
              label="Labor Minimum"
              value={formData.laborMinimum}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          {/* Additional Costs */}
          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="material"
              label="Material ($)"
              type="number"
              value={formData.material}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="equipment"
              label="Equipment"
              type="number"
              value={formData.equipment}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="marketCondition"
              label="Market Condition ($)"
              type="number"
              value={formData.marketCondition}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="salesTax"
              label="Sales Tax ($)"
              type="number"
              value={formData.salesTax}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          {/* Financial Details Section */}
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: 18,
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.main,
                mb: 1
              }}
            >
              Financial Details
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="rcv"
              label="RCV ($)"
              type="number"
              value={formData.rcv}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="life"
              label="Life"
              type="number"
              value={formData.life}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <CustomDropdown
              label="Depreciation"
              value={formData.depreciation}
              onChange={(e) => handleDropdownChange('depreciation', e.target.value)}
              options={depreciaationListData.map((val) => ({
                value: val.resource_id,
                label: val.name
              }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <InputField
              name="depreciationAmount"
              label="Depreciation Amount"
              type="number"
              value={formData.depreciationAmount}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              label="Recoverable"
              value={formData.recoverable}
              onChange={(e) => handleDropdownChange('recoverable', e.target.value)}
              options={confirmationListData.map((val) => ({
                value: val.resource_id,
                label: val.name
              }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="acv"
              label="ACV ($)"
              type="number"
              value={formData.acv}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              label="Activity"
              value={formData.activity}
              onChange={(e) => handleDropdownChange('activity', e.target.value)}
              options={activityListData.map((val) => ({ value: val.resource_id, label: val.name }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          {/* Options Section */}
          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              label="Tax"
              value={formData.tax}
              onChange={(e) => handleDropdownChange('tax', e.target.value)}
              options={confirmationListData.map((val) => ({
                value: val.resource_id,
                label: val.name
              }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              label="Replace"
              value={formData.replace}
              onChange={(e) => handleDropdownChange('replace', e.target.value)}
              options={confirmationListData.map((val) => ({
                value: val.resource_id,
                label: val.name
              }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          {/* Additional Information Section */}
          <Grid item xs={12}>
            <Typography
              sx={{
                fontSize: 18,
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.main,
                mb: 1
              }}
            >
              Additional Information
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <CustomDropdown
              name="cat"
              label="Category"
              value={formData.cat}
              onChange={(e) => handleDropdownChange('cat', e.target.value)}
              options={categoryListData.map((val) => ({ value: val.resource_id, label: val.name }))}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="sel"
              label="Selection"
              value={formData.sel}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputField
              name="owner"
              label="Owner"
              value={formData.owner}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <InputField
              name="originalVendor"
              label="Original Vendor"
              value={formData.originalVendor}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <InputField
              name="sourceName"
              label="Source Name"
              value={formData.sourceName}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Box sx={{ mb: { xs: 2, sm: 2 }, position: 'relative' }}>
              <Typography
                sx={{
                  fontSize: 12,
                  marginBottom: '4px',
                  fontFamily: 'poppinsMedium',
                  color: palette.primary.titledark
                }}
              >
                {'Date'}
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.date}
                  onChange={(val) => handleDateChange('date', val)}
                  label=""
                  slotProps={{
                    textField: {
                      size: isMobile ? 'small' : 'medium',
                      placeholder: '',
                      fullWidth: true,
                      error: Boolean(errors.date),
                      helperText: errors.date,
                      sx: {
                        height: 30,
                        mb: errors.date ? 2 : 0,
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        '& .MuiInputBase-input': {
                          fontSize: 11,
                          fontFamily: 'poppinsRegular',
                          padding: '12px 12px'
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: errors.date ? '#f44336' : palette.primary.inputborder,
                            borderWidth: '1.5px',
                            borderRadius: '6px'
                          },
                          '&:hover fieldset': {
                            borderColor: errors.date ? '#f44336' : palette.primary.inputborder
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: errors.date ? '#f44336' : palette.primary.inputborder
                          }
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: 11,
                          fontFamily: 'poppinsRegular',
                          ml: 0
                        }
                      }
                    },
                    openPickerButton: {
                      sx: {
                        color: errors.date ? '#f44336' : palette.primary.dropdownArrow
                      }
                    }
                  }}
                  slots={{
                    openPickerIcon: CalendarMonthIcon
                  }}
                />
              </LocalizationProvider>

              {/* error icon input ke upar chipka diya */}
              {errors.date && (
                <ErrorIcon
                  color="error"
                  sx={{
                    position: 'absolute',
                    right: 40, // calendar button ke sath adjust karne ke liye
                    top: 32, // label ke niche align karne ke liye
                    fontSize: 20,
                    pointerEvents: 'none'
                  }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <InputField
              name="adjSource"
              label="ADJ Source"
              value={formData.adjSource}
              onChange={handleChange}
              backgroundColor="#fff"
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>

          <Grid item xs={12}>
            <MultilineInputField
              name="notes"
              label="Notes"
              value={formData.notes}
              onChange={handleChange}
              backgroundColor="#fff"
              rows={3}
              maxRows={8}
              fullWidth
              marginBottom="0.5rem"
              labelFontMedium
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            ml: 1.5,
            color: palette.primary.pageButtonTitle,
            borderColor: palette.primary.pageButtonBorder,
            fontSize: 12,
            height: 30,
            fontFamily: 'poppinsRegular',
            borderRadius: 0.7,
            '&:hover': {
              backgroundColor: palette.primary.inputborder,
              borderColor: palette.primary.pageButtonBorder
            }
          }}
        >
          Cancel
        </Button>

        <LoadingButtonWrapper
          loading={saveLoading}
          onClick={handleSave}
          variant="contained"
          sx={{
            width: saveLoading ? 120 : 90,
            height: 30,
            fontSize: 12,
            borderRadius: 0.7,
            fontFamily: 'poppinsLight',
            letterSpacing: 0.3,
            transition: 'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
          }}
          title="Save"
        />
      </DialogActions>
    </Dialog>
  );
};

export default AddEditTaskModal;
